import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface DayStatus {
  date: string;
  isComplete: boolean | null;
  hasData: boolean;
}

interface WeekCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
}

export default function WeekCalendar({ selectedDate, onDateSelect }: WeekCalendarProps) {
  const [weekDays, setWeekDays] = useState<DayStatus[]>([]);
  const [loading, setLoading] = useState(true);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    loadWeekData();
  }, [selectedDate]);

  const getWeekDays = () => {
    const today = new Date();
    const days: Date[] = [];

    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const formatDateForDB = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDateString = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const loadWeekData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const days = getWeekDays();
      const startDate = formatDateForDB(days[0]);
      const endDate = formatDateForDB(days[6]);

      const { data: protocols } = await supabase
        .from('daily_protocols')
        .select('date, fasting_completed, nutrition_tracked, exercise_completed')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .lte('date', endDate);

      const protocolMap = new Map(
        protocols?.map(p => [p.date, p]) || []
      );

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dayStatuses: DayStatus[] = days.map(day => {
        const dateStr = formatDateForDB(day);
        const protocol = protocolMap.get(dateStr);
        const dayStart = new Date(day);
        dayStart.setHours(0, 0, 0, 0);

        const isPast = dayStart < today;
        const isToday = dayStart.getTime() === today.getTime();

        if (protocol) {
          const allComplete = protocol.fasting_completed &&
                             protocol.nutrition_tracked &&
                             protocol.exercise_completed;
          return {
            date: dateStr,
            isComplete: allComplete,
            hasData: true
          };
        }

        return {
          date: dateStr,
          isComplete: isPast && !isToday ? false : null,
          hasData: false
        };
      });

      setWeekDays(dayStatuses);
    } catch (error) {
      console.error('Error loading week data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isToday = (dateStr: string) => {
    const today = new Date();
    return formatDateForDB(today) === dateStr;
  };

  const isSelected = (dateStr: string) => {
    return formatDateForDB(selectedDate) === dateStr;
  };

  const isFuture = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = parseDateString(dateStr);
    return date > today;
  };

  const getCircleClasses = (day: DayStatus) => {
    const today = isToday(day.date);
    const selected = isSelected(day.date);
    const future = isFuture(day.date);

    let classes = 'w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-200';

    if (future) {
      classes += ' bg-gray-100 text-gray-400';
    } else if (selected) {
      if (today) {
        classes += ' bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/40 scale-110';
      } else if (day.isComplete === true) {
        classes += ' bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/40 scale-110';
      } else if (day.isComplete === false) {
        classes += ' bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg shadow-red-500/40 scale-110';
      } else {
        classes += ' bg-gray-700 text-white shadow-lg scale-110';
      }
    } else if (today) {
      classes += ' bg-cyan-50 text-cyan-600 ring-2 ring-cyan-500';
    } else if (day.isComplete === true) {
      classes += ' bg-green-50 text-green-600 ring-2 ring-green-400';
    } else if (day.isComplete === false) {
      classes += ' bg-red-50 text-red-500 ring-2 ring-red-300';
    } else {
      classes += ' bg-gray-100 text-gray-700';
    }

    return classes;
  };

  const handleDateClick = (day: DayStatus) => {
    if (isFuture(day.date)) return;
    const date = parseDateString(day.date);
    onDateSelect(date);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-4 border border-gray-200">
        <div className="flex justify-between">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="flex flex-col items-center px-1">
              <div className="w-6 h-3 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const days = getWeekDays();

  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-200">
      <div className="flex justify-between">
        {weekDays.map((day, index) => {
          const dayDate = days[index];
          const future = isFuture(day.date);
          const today = isToday(day.date);

          return (
            <button
              key={day.date}
              type="button"
              disabled={future}
              onClick={() => handleDateClick(day)}
              className={`flex flex-col items-center px-1 py-1 rounded-xl transition-opacity ${
                future ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer active:opacity-70'
              }`}
              aria-label={`${dayNames[dayDate.getDay()]} ${dayDate.getDate()}`}
            >
              <span className={`text-[10px] font-bold uppercase mb-1.5 ${
                today ? 'text-cyan-600' : 'text-gray-400'
              }`}>
                {dayNames[dayDate.getDay()]}
              </span>
              <div className={getCircleClasses(day)}>
                {dayDate.getDate()}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}